import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, Alert, Modal, Linking, Image, Platform } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import ManageWorkersModal from '../components/ManageWorkersModal';
import EditAppointmentModal from '../components/EditAppointmentModal';
import DurationTrackingModal from '../components/DurationTrackingModal';
import InvoiceModal from '../components/InvoiceModal';
import AppointmentPaymentModal from '../components/AppointmentPaymentModal';
import SMSTestModal from '../components/SMSTestModal';
import FrontDeskQRModal from '../components/FrontDeskQRModal';
import BusinessOwnerBottomMenu from '../components/BusinessOwnerBottomMenu';
import AppointmentsScreen from './AppointmentsScreen';
import AnalyticsScreen from './AnalyticsScreen';
import TrialExpiredModal from '../components/TrialExpiredModal';
import MobileDataService from '../services/mobileDataService';
import { formatServicePriceShort, hasListedServicePrice } from '../utils/servicePriceDisplay';
import DataSyncService from '../services/dataSyncService';
// import { firebaseService } from '../services/firebaseService';
// Temporarily disabled to fix render error
// import PaymentRequestScreen from './PaymentRequestScreen';
import { appointmentManager, Appointment as ManagerAppointment } from '../utils/appointmentManager';
import { customServiceRequestService } from '../services/customServiceRequestService';
import { TrialManager, TrialData } from '../utils/trialManager';
import workerService from '../services/workerService';
import { AuthManager } from '../utils/authManager';
import { buildFrontDeskLinks } from '../utils/frontDeskLinks';

interface Appointment {
  id: string;
  customerName: string;
  phone: string;
  email: string;
  address: string;
  serviceType: string;
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleMileage: string;
  vehicleVin: string;
  vehicleVinPhoto: string;
  vehicleType: string;
  serviceHistory: string;
  specialInstructions: string;
  appointmentDate: string;
  appointmentTime: string;
  status: string;
  workerId?: string;
  workerName?: string;
  actualDuration?: number;
  paymentData?: any;
}

interface Worker {
  id: string;
  name: string;
  phone: string;
  email: string;
  specialties: string;
}

interface DashboardScreenProps {
  onLogout: () => void;
  onNavigateToSettings: () => void;
  onNavigateToServiceManagement: () => void;
  onNavigateToProductManagement: () => void;
  onNavigateToQRCode: () => void;
  onNavigateToCustomerManagement?: () => void;
  onNavigateToBankSetup?: () => void;
  onNavigateToPayoutDashboard?: () => void;
  onNavigateToBusinessChat?: () => void;
  onNavigateToSendInvoice?: () => void;
  onNavigateToWidgetDownload?: () => void;
  onNavigateToQuoteRequests?: () => void;
  onNavigateToPackageSelection?: () => void;
  username?: string;
}

const DashboardScreen: React.FC<DashboardScreenProps> = ({ onLogout, onNavigateToSettings, onNavigateToServiceManagement, onNavigateToProductManagement, onNavigateToQRCode, onNavigateToCustomerManagement, onNavigateToBankSetup, onNavigateToPayoutDashboard, onNavigateToBusinessChat, onNavigateToSendInvoice, onNavigateToWidgetDownload, onNavigateToQuoteRequests, onNavigateToPackageSelection, username }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showManageWorkers, setShowManageWorkers] = useState(false);
  const [showEditAppointment, setShowEditAppointment] = useState(false);
  const [showDurationTracking, setShowDurationTracking] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showPaymentRequest, setShowPaymentRequest] = useState(false);
  const [showInvoiceSelection, setShowInvoiceSelection] = useState(false);
  const [showInvoicePriceInput, setShowInvoicePriceInput] = useState(false);
  const [showInvoiceDeliveryMethod, setShowInvoiceDeliveryMethod] = useState(false);
  const [selectedAppointmentForInvoice, setSelectedAppointmentForInvoice] = useState<Appointment | null>(null);
  const [invoicePrice, setInvoicePrice] = useState('');
  const [selectedService, setSelectedService] = useState<any>(null);
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [showCreateAppointment, setShowCreateAppointment] = useState(false);
  const [showSMSTest, setShowSMSTest] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    appointmentDate: '',
    appointmentTime: '',
  });
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] = useState<Appointment | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'appointments' | 'analytics'>('dashboard');
  const [showTrialExpired, setShowTrialExpired] = useState(false);
  const [trialData, setTrialData] = useState<TrialData | null>(null);
  const [businessOwnerId, setBusinessOwnerId] = useState<string>('');
  const [services, setServices] = useState<any[]>([]);
  const [businessOwnerName, setBusinessOwnerName] = useState<string>('');
  const [currentPlan, setCurrentPlan] = useState<string>('Free');
  const [showFrontDeskQRModal, setShowFrontDeskQRModal] = useState(false);
  const [frontDeskBookingUrl, setFrontDeskBookingUrl] = useState('');
  const [frontDeskSignUrl, setFrontDeskSignUrl] = useState('');
  const [pendingQuoteRequests, setPendingQuoteRequests] = useState<number>(0);

  // Initialize shared data service
  const dataService = MobileDataService.getInstance();
  const syncService = DataSyncService.getInstance();

  // Load data on mount
  useEffect(() => {
    loadBusinessData();
    // checkTrialStatus(); // DISABLED FOR DEMO
    
    // Cleanup sync service on unmount
    return () => {
      syncService.stopAutoSync();
    };
  }, []);

  const loadBusinessData = async () => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (businessOwner) {
        const businessId = businessOwner.id;
        setBusinessOwnerId(businessId);
        setBusinessOwnerName(businessOwner.companyName || businessOwner.name || username || 'Your Business');
        console.log('📋 DashboardScreen: Business owner ID:', businessId);
        
        // Get current plan
        const subscriptionData = await TrialManager.getSubscriptionData();
        if (subscriptionData && subscriptionData.planType) {
          const planNames: { [key: string]: string } = {
            'free': 'Free',
            'starter': 'Starter',
            'professional': 'Professional',
            'business': 'Business'
          };
          setCurrentPlan(planNames[subscriptionData.planType] || 'Free');
        } else {
          setCurrentPlan('Free');
        }
        
        // Load appointments filtered by business owner ID
        const allAppointments = await appointmentManager.getAppointments();
        const filteredAppointments = allAppointments.filter(apt => {
          // Filter by businessOwnerId if it exists, otherwise show empty for new accounts
          return (apt as any).businessOwnerId === businessId;
        });
        console.log(`📋 DashboardScreen: Filtered ${filteredAppointments.length} appointments for business owner ${businessId} out of ${allAppointments.length} total`);
        setAppointments(filteredAppointments);
        
        // Load services
        const localServices = dataService.getServices(businessId);
        setServices(localServices);
        
        // Load workers from storage
        const savedWorkers = await workerService.getWorkers(businessId);
        
        if (savedWorkers.length > 0) {
          console.log(`✅ DashboardScreen: Loaded ${savedWorkers.length} workers from storage`);
          setWorkers(savedWorkers);
        } else {
          // Determine if this is a solo business based on company size
          const isSoloBusiness = businessOwner.companySize === '1-5 employees' || 
                                businessOwner.companySize === 'Solo' ||
                                businessOwner.companySize === 'Just me';
          
          console.log(`📋 DashboardScreen: No workers found, initializing ${isSoloBusiness ? 'solo business owner' : 'default workers'}...`);
          await workerService.initializeDefaultWorkers(
            businessId, 
            businessOwner.companyName || businessOwner.name,
            isSoloBusiness
          );
          const defaultWorkers = await workerService.getWorkers(businessId);
          setWorkers(defaultWorkers);
        }
        
        // Start auto-sync
        syncService.startAutoSync();

        // Load pending quote/custom-service request count for badge
        try {
          const quoteRequests = await customServiceRequestService.getRequestsForBusiness(businessId);
          setPendingQuoteRequests(quoteRequests.filter(r => r.status === 'pending').length);
        } catch (quoteErr) {
          console.warn('DashboardScreen: failed to load quote requests:', quoteErr);
          setPendingQuoteRequests(0);
        }
      } else {
        // No business owner found, show empty
        setAppointments([]);
        setWorkers([]);
        setServices([]);
        setPendingQuoteRequests(0);
      }
    } catch (error) {
      console.error('Error loading business data:', error);
      // Fallback to empty data for production
      setAppointments([]);
      setWorkers([]);
      setServices([]);
      setPendingQuoteRequests(0);
    }
  };

  const checkTrialStatus = async () => {
    try {
      // Skip trial check for demo mode
      console.log('🎭 Demo mode: Skipping trial check');
      return;
      
      const isSubscribed = await TrialManager.isSubscribed();
      if (isSubscribed) {
        console.log('✅ User has active subscription');
        return;
      }

      const trialData = await TrialManager.getTrialData();
      if (trialData) {
        setTrialData(trialData);
        if (trialData.isExpired) {
          console.log('⏰ Trial has expired');
          setShowTrialExpired(true);
        }
      }
    } catch (error) {
      console.error('Error checking trial status:', error);
    }
  };

  const handleTabPress = (tab: 'dashboard' | 'appointments' | 'analytics') => {
    setActiveTab(tab);
  };

  const handleTrialUpgrade = async () => {
    try {
      const success = await TrialManager.upgradeToPaid('professional');
      if (success) {
        setShowTrialExpired(false);
        Alert.alert('Success', 'You have been upgraded to Professional plan!');
      } else {
        Alert.alert('Error', 'Failed to upgrade. Please try again.');
      }
    } catch (error) {
      console.error('Error upgrading:', error);
      Alert.alert('Error', 'Failed to upgrade. Please try again.');
    }
  };

  const handleTrialLogout = () => {
    setShowTrialExpired(false);
    onLogout();
  };

  // Removed loadSampleData() - no hardcoded workers/appointments for production

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadBusinessData();
      // await checkTrialStatus(); // DISABLED FOR DEMO
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    console.log('📝 Edit appointment clicked for:', appointment.customerName);
    console.log('📝 Appointment status:', appointment.status);
    console.log('📝 Current showEditAppointment state:', showEditAppointment);
    console.log('📝 Setting showEditAppointment to true');
    
    
    setSelectedAppointment(appointment);
    setShowEditAppointment(true);
    console.log('📝 showEditAppointment should now be true');
  };

  const handleTrackDuration = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowDurationTracking(true);
  };

  const handleChatWithCustomer = (appointment: Appointment) => {
    // For now, just show an alert. In a real implementation, this would navigate to chat
    Alert.alert(
      'Chat with Customer',
      `Start a conversation with ${appointment.customerName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Start Chat', 
          onPress: () => {
            // This would navigate to a chat screen with the customer
            // For now, we'll just show a success message
            Alert.alert('Chat Started', `You can now chat with ${appointment.customerName}`);
          }
        }
      ]
    );
  };

  const handleProcessPayment = (appointment: Appointment) => {
    console.log('💳 Process payment clicked for:', appointment.customerName);
    console.log('💳 Appointment status:', appointment.status);
    console.log('💳 Current showPaymentOptions state:', showPaymentOptions);
    console.log('💳 Setting showPaymentOptions to true');
    
    
    setSelectedAppointmentForPayment(appointment);
    setShowPaymentOptions(true);
    console.log('💳 showPaymentOptions should now be true');
  };

  const buildFrontDeskLinksForBusiness = () => {
    return buildFrontDeskLinks(
      businessOwnerId || username || 'business',
      businessOwnerName || 'Your Business'
    );
  };

  const handleOpenFrontDeskQR = () => {
    if (!businessOwnerId) {
      Alert.alert(
        'Business profile loading',
        'Your business ID is still loading. Wait a moment and try again.'
      );
      return;
    }
    const { bookingUrl, signUrl } = buildFrontDeskLinksForBusiness();
    setFrontDeskBookingUrl(bookingUrl);
    setFrontDeskSignUrl(signUrl);
    setShowFrontDeskQRModal(true);
  };

  const handleSendPaymentRequest = () => {
    setShowPaymentRequest(true);
  };

  const handleSendInvoice = () => {
    console.log('🔍 handleSendInvoice called, onNavigateToSendInvoice:', !!onNavigateToSendInvoice);
    if (onNavigateToSendInvoice) {
      console.log('✅ Navigating to Send Invoice screen');
      onNavigateToSendInvoice();
    } else {
      console.log('⚠️ onNavigateToSendInvoice not available, showing modal');
      setShowInvoiceSelection(true);
    }
  };

  const handleSelectAppointmentForInvoice = (appointment: Appointment) => {
    setSelectedAppointmentForInvoice(appointment);
    setInvoicePrice(appointment.estimatedCost?.toString() || '');
    setShowInvoiceSelection(false);
    setShowInvoicePriceInput(true);
  };

  const handleConfirmInvoicePrice = () => {
    if (!invoicePrice || parseFloat(invoicePrice) <= 0) {
      Alert.alert('Invalid Price', 'Please enter a valid price greater than 0');
      return;
    }
    setShowInvoicePriceInput(false);
    setShowInvoiceDeliveryMethod(true);
  };

  const handleSelectService = (service: any) => {
    setSelectedService(service);
    setInvoicePrice(hasListedServicePrice(service.price) ? service.price!.toString() : '');
    setShowServiceSelection(false);
  };

  const handleManualPriceEntry = () => {
    setSelectedService(null);
    setInvoicePrice('');
    setShowServiceSelection(false);
  };

  const handleCreateNewAppointment = () => {
    setShowInvoiceSelection(false);
    setShowCreateAppointment(true);
  };

  const handleCreateAppointment = async () => {
    if (!newCustomerData.name || !newCustomerData.phone || !newCustomerData.serviceType) {
      Alert.alert('Missing Information', 'Please fill in customer name, phone, and service type');
      return;
    }

    try {
      // Create a new appointment using shared data service
      const newAppointment = await dataService.createAppointment({
        customerName: newCustomerData.name,
        phone: newCustomerData.phone,
        email: newCustomerData.email,
        serviceType: newCustomerData.serviceType,
        appointmentDate: newCustomerData.appointmentDate || new Date().toLocaleDateString(),
        appointmentTime: newCustomerData.appointmentTime || '10:00 AM',
        estimatedCost: 0, // Will be set in price input
        status: 'confirmed',
        notes: 'Created for invoice sending',
        businessOwnerId: businessOwnerId
      });

      // Update local state
      setAppointments(prev => [...prev, newAppointment]);

      // Set the new appointment and proceed to price input
      setSelectedAppointmentForInvoice(newAppointment);
      setInvoicePrice('');
      setShowCreateAppointment(false);
      setShowInvoicePriceInput(true);
    } catch (error) {
      console.error('Error creating appointment:', error);
      Alert.alert('Error', 'Failed to create appointment. Please try again.');
    }
  };

  const handleInvoiceDeliveryMethod = async (method: 'email' | 'sms' | 'chat') => {
    if (!selectedAppointmentForInvoice) return;

    try {
      setShowInvoiceDeliveryMethod(false);
      
      let success = false;
      let message = '';

      switch (method) {
        case 'email':
          success = await sendInvoiceToCustomer(selectedAppointmentForInvoice);
          message = `Invoice has been sent to ${selectedAppointmentForInvoice.customerName} via email`;
          break;
        case 'sms':
          success = await sendInvoiceViaSMS(selectedAppointmentForInvoice);
          message = `Invoice has been sent to ${selectedAppointmentForInvoice.customerName} via SMS`;
          break;
        case 'chat':
          success = await sendInvoiceViaChat(selectedAppointmentForInvoice);
          message = `Invoice has been sent to ${selectedAppointmentForInvoice.customerName} via in-app chat`;
          break;
      }

      if (success) {
        Alert.alert('Invoice Sent!', message, [{ text: 'OK' }]);
      } else {
        Alert.alert('Error', 'Failed to send invoice. Please try again.');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      Alert.alert('Error', 'An error occurred while sending the invoice.');
    }
  };

  const sendInvoiceToCustomer = async (appointment: Appointment): Promise<boolean> => {
    try {
      // Import email service
      const { emailService } = await import('../services/emailService');
      
      // Get business owner details
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) {
        throw new Error('Business owner not found');
      }

      // Generate invoice data
      const invoiceData = {
        invoiceNumber: `INV-${Date.now()}`,
        businessName: businessOwner.companyName || businessOwner.name || 'Elite Hair Studio',
        businessEmail: businessOwner.email || 'info@elitehairstudio.com',
        businessPhone: businessOwner.phone || '',
        businessAddress: businessOwner.address || '123 Main St, City, State 12345',
        customerName: appointment.customerName,
        customerEmail: appointment.email,
        serviceDate: appointment.appointmentDate,
        serviceTime: appointment.appointmentTime,
        serviceType: appointment.serviceType,
        serviceDescription: `Professional ${appointment.serviceType.toLowerCase()} service`,
        amount: parseFloat(invoicePrice) || appointment.amount || 45.00,
        taxRate: 0.08, // 8% tax
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString() // 7 days from now
      };

      // Calculate totals
      const subtotal = invoiceData.amount;
      const tax = subtotal * invoiceData.taxRate;
      const total = subtotal + tax;

      // Send invoice email
      const success = await emailService.sendInvoice({
        to: appointment.email,
        customerName: appointment.customerName,
        invoiceNumber: invoiceData.invoiceNumber,
        businessName: invoiceData.businessName,
        businessEmail: invoiceData.businessEmail,
        businessPhone: invoiceData.businessPhone,
        businessAddress: invoiceData.businessAddress,
        serviceDate: invoiceData.serviceDate,
        serviceTime: invoiceData.serviceTime,
        serviceType: invoiceData.serviceType,
        serviceDescription: invoiceData.serviceDescription,
        subtotal: subtotal,
        tax: tax,
        total: total,
        dueDate: invoiceData.dueDate
      });

      return success;
    } catch (error) {
      console.error('Error sending invoice:', error);
      return false;
    }
  };

  const sendInvoiceViaSMS = async (appointment: Appointment): Promise<boolean> => {
    try {
      // Import SMS service
      const { smsService } = await import('../services/smsService');
      
      // Get business owner details
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) {
        throw new Error('Business owner not found');
      }

      // Generate invoice data
      const invoiceData = {
        invoiceNumber: `INV-${Date.now()}`,
        businessName: businessOwner.companyName || businessOwner.name || 'Elite Hair Studio',
        businessPhone: businessOwner.phone || '',
        customerName: appointment.customerName,
        serviceDate: appointment.appointmentDate,
        serviceTime: appointment.appointmentTime,
        serviceType: appointment.serviceType,
        amount: appointment.amount || 45.00,
        taxRate: 0.08,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
      };

      // Calculate totals
      const subtotal = invoiceData.amount;
      const tax = subtotal * invoiceData.taxRate;
      const total = subtotal + tax;

      // Create SMS message
      const smsMessage = `📧 INVOICE #${invoiceData.invoiceNumber}

${invoiceData.businessName}

Dear ${invoiceData.customerName},

Service: ${invoiceData.serviceType}
Date: ${invoiceData.serviceDate} at ${invoiceData.serviceTime}

Amount: $${subtotal}
Tax (8%): $${tax}
TOTAL: $${total}

Due Date: ${invoiceData.dueDate}

Pay online: https://bookapt.io/pay/${invoiceData.invoiceNumber}

Questions? Call ${invoiceData.businessPhone}

Thank you!`;

      // Send SMS
      const success = await smsService.sendSMS(appointment.phone, smsMessage, 'invoice');
      return success;
    } catch (error) {
      console.error('Error sending invoice via SMS:', error);
      return false;
    }
  };

  const sendInvoiceViaChat = async (appointment: Appointment): Promise<boolean> => {
    try {
      // Get business owner details
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) {
        throw new Error('Business owner not found');
      }

      // Generate invoice data
      const invoiceData = {
        invoiceNumber: `INV-${Date.now()}`,
        businessName: businessOwner.companyName || businessOwner.name || 'Elite Hair Studio',
        businessPhone: businessOwner.phone || '',
        customerName: appointment.customerName,
        serviceDate: appointment.appointmentDate,
        serviceTime: appointment.appointmentTime,
        serviceType: appointment.serviceType,
        amount: appointment.amount || 45.00,
        taxRate: 0.08,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
      };

      // Calculate totals
      const subtotal = invoiceData.amount;
      const tax = subtotal * invoiceData.taxRate;
      const total = subtotal + tax;

      // Create chat message
      const chatMessage = {
        type: 'invoice',
        text: `📧 Invoice #${invoiceData.invoiceNumber}`,
        invoiceData: {
          invoiceNumber: invoiceData.invoiceNumber,
          businessName: invoiceData.businessName,
          customerName: invoiceData.customerName,
          serviceType: invoiceData.serviceType,
          serviceDate: invoiceData.serviceDate,
          serviceTime: invoiceData.serviceTime,
          subtotal: subtotal,
          tax: tax,
          total: total,
          dueDate: invoiceData.dueDate,
          businessPhone: invoiceData.businessPhone
        }
      };

      // Send chat message (integrate with existing chat system)
      await sendChatMessage({
        customerId: appointment.customerId || `customer_${appointment.id}`,
        businessId: businessOwner.id,
        message: chatMessage
      });

      return true;
    } catch (error) {
      console.error('Error sending invoice via chat:', error);
      return false;
    }
  };

  const sendChatMessage = async (messageData: any): Promise<void> => {
    // This would integrate with the existing chat system
    console.log('Sending chat message:', messageData);
    // In production, this would send the message through the chat service
  };

  const handleSaveAppointment = (updatedAppointment: Appointment) => {
    setAppointments(prev => 
      prev.map(apt => apt.id === updatedAppointment.id ? updatedAppointment : apt)
    );
    setShowEditAppointment(false);
    setSelectedAppointment(null);
  };

  const handleSaveDuration = (appointmentId: string, duration: number) => {
    setAppointments(prev =>
      prev.map(apt => 
        apt.id === appointmentId ? { ...apt, actualDuration: duration } : apt
      )
    );
    setShowDurationTracking(false);
    setSelectedAppointment(null);
  };

  const handleInvoiceSent = () => {
    setShowInvoice(false);
    setSelectedAppointment(null);
    Alert.alert('Success', 'Invoice sent successfully!');
  };

  const handlePaymentSuccess = (updatedAppointment: ManagerAppointment) => {
    setShowPaymentOptions(false);
    setSelectedAppointmentForPayment(null);

    setAppointments(prev =>
      prev.map(apt =>
        apt.id === updatedAppointment.id ? { ...apt, ...updatedAppointment } as Appointment : apt
      )
    );

    Alert.alert('Success', 'Payment processed successfully!');
  };

  const handleDeleteWorker = async (workerId: string) => {
    Alert.alert(
      'Delete Worker',
      'Are you sure you want to delete this worker?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await workerService.deleteWorker(businessOwnerId, workerId);
            setWorkers(prev => prev.filter(w => w.id !== workerId));
              Alert.alert('Success', 'Worker deleted successfully');
            } catch (error) {
              console.error('Error deleting worker:', error);
              Alert.alert('Error', 'Failed to delete worker. Please try again.');
            }
          },
        },
      ]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return '#28a745';
      case 'completed': return '#007bff';
      case 'paid': return '#6f42c1';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmed';
      case 'completed': return 'Completed';
      case 'paid': return 'Paid';
      case 'cancelled': return 'Cancelled';
      default: return 'Pending';
    }
  };

  // Show Appointments screen when appointments tab is selected
  if (activeTab === 'appointments') {
    return (
      <AppointmentsScreen
        onBack={() => setActiveTab('dashboard')}
        username={username}
        onTabPress={handleTabPress}
        onNavigateToSettings={onNavigateToSettings}
        onLogout={onLogout}
      />
    );
  }

  // Show Analytics screen when analytics tab is selected
  if (activeTab === 'analytics') {
    return (
      <AnalyticsScreen
        onBack={() => setActiveTab('dashboard')}
        username={username}
        onTabPress={handleTabPress}
        onNavigateToSettings={onNavigateToSettings}
        onLogout={onLogout}
      />
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <Text style={styles.greeting}>Hello,</Text>
              <Text style={styles.userName}>{businessOwnerName}!</Text>
              <View style={styles.planContainer}>
                <Text style={styles.planText}>{currentPlan} Plan</Text>
                {onNavigateToPackageSelection && (
                  <TouchableOpacity
                    onPress={onNavigateToPackageSelection}
                    style={styles.upgradeButton}
                  >
                    <Text style={styles.upgradeButtonText}>Upgrade</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Web Version Banner - Mobile Only */}
        {Platform.OS !== 'web' && (
          <View style={styles.webVersionBanner}>
            <View style={styles.webVersionBannerContent}>
              <Text style={styles.webVersionBannerIcon}>💻</Text>
              <View style={styles.webVersionBannerText}>
                <Text style={styles.webVersionBannerTitle}>Also available on web!</Text>
                <Text style={styles.webVersionBannerSubtitle}>Visit bookapt.io for desktop access</Text>
              </View>
              <TouchableOpacity
                style={styles.webVersionBannerButton}
                onPress={() => {
                  Linking.openURL('https://bookapt.io/').catch(err => {
                    Alert.alert('Error', 'Could not open website. Please visit bookapt.io in your browser.');
                  });
                }}
              >
                <Text style={styles.webVersionBannerButtonText}>Visit</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Stats */}
        <View style={styles.statsContainer}>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>{appointments.length}</Text>
            <Text style={styles.statLabel}>Total Appointments</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>
              {appointments.filter(apt => apt.status === 'confirmed').length}
            </Text>
            <Text style={styles.statLabel}>Confirmed</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>
              {appointments.filter(apt => apt.status === 'completed').length}
            </Text>
            <Text style={styles.statLabel}>Completed</Text>
          </View>
        </View>

        {/* Quick Actions - 3 per row */}
        <View style={styles.quickActionsSection}>
          <Text style={styles.sectionTitle}>Quick Actions</Text>
          <View style={styles.quickActionsContainer}>
            <TouchableOpacity
              style={styles.quickActionButton}
              onPress={onNavigateToServiceManagement}
            >
              <Text style={styles.quickActionIcon}>💇‍♀️</Text>
              <Text style={styles.quickActionText}>Services</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickActionButton}
              onPress={onNavigateToProductManagement}
            >
              <Text style={styles.quickActionIcon}>🛒</Text>
              <Text style={styles.quickActionText}>Products</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickActionButton}
              onPress={onNavigateToCustomerManagement}
            >
              <Text style={styles.quickActionIcon}>🙋</Text>
              <Text style={styles.quickActionText}>Customers</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickActionButton}
              onPress={() => setShowManageWorkers(true)}
            >
              <Text style={styles.quickActionIcon}>👥</Text>
              <Text style={styles.quickActionText}>Add Worker</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickActionButton}
              onPress={onNavigateToSettings}
            >
              <Text style={styles.quickActionIcon}>⚙️</Text>
              <Text style={styles.quickActionText}>Settings</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickActionButton}
              onPress={onNavigateToWidgetDownload}
            >
              <Text style={styles.quickActionIcon}>📦</Text>
              <Text style={styles.quickActionText}>Download Widget</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickActionButton}
              onPress={handleOpenFrontDeskQR}
            >
              <Text style={styles.quickActionIcon}>📍</Text>
              <Text style={styles.quickActionText}>Front Desk QR</Text>
            </TouchableOpacity>
            {!!onNavigateToQuoteRequests && (
              <TouchableOpacity
                style={styles.quickActionButton}
                onPress={onNavigateToQuoteRequests}
              >
                <View style={{ position: 'relative' }}>
                  <Text style={styles.quickActionIcon}>📨</Text>
                  {pendingQuoteRequests > 0 && (
                    <View style={styles.quoteRequestBadge}>
                      <Text style={styles.quoteRequestBadgeText}>
                        {pendingQuoteRequests > 9 ? '9+' : pendingQuoteRequests}
                      </Text>
                    </View>
                  )}
                </View>
                <Text style={styles.quickActionText}>Quote Requests</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Powered by ORYN Inc. Footer - Always at bottom */}
      <View style={styles.poweredByContainer}>
        <TouchableOpacity 
          onPress={() => Linking.openURL('https://orynsolutions.io')}
          activeOpacity={0.7}
        >
          <Text style={styles.poweredByText}>Powered by Oryn Inc.</Text>
        </TouchableOpacity>
      </View>

      {/* Modals */}
      <ManageWorkersModal
        visible={showManageWorkers}
        onClose={() => setShowManageWorkers(false)}
        workers={workers}
        businessId={businessOwnerId}
        businessName={businessOwnerName}
        invitedByUserId={businessOwnerId}
        onAddWorker={async (worker) => {
          const newWorker = await workerService.addWorker(businessOwnerId, worker as any);
          setWorkers(prev => [...prev, newWorker]);
          return newWorker as any;
        }}
        onDeleteWorker={handleDeleteWorker}
      />

      <EditAppointmentModal
        visible={showEditAppointment}
        onClose={() => {
          console.log('📝 Closing EditAppointmentModal');
          setShowEditAppointment(false);
          setSelectedAppointment(null);
        }}
        booking={selectedAppointment}
        workers={workers}
        onSave={handleSaveAppointment}
      />

      <DurationTrackingModal
        visible={showDurationTracking}
        onClose={() => {
          setShowDurationTracking(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onSave={handleSaveDuration}
      />

      <InvoiceModal
        visible={showInvoice}
        onClose={() => {
          setShowInvoice(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onSend={handleInvoiceSent}
      />

      <AppointmentPaymentModal
        visible={showPaymentOptions}
        onClose={() => {
          console.log('💳 Closing AppointmentPaymentModal');
          setShowPaymentOptions(false);
          setSelectedAppointmentForPayment(null);
        }}
        appointment={selectedAppointmentForPayment}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Temporarily disabled PaymentRequestScreen to fix render error */}
      {/* <PaymentRequestScreen
        visible={showPaymentRequest}
        onBack={() => setShowPaymentRequest(false)}
        businessId={username || 'test_business_1'}
      /> */}

      {/* Invoice Selection Modal */}
      <Modal
        visible={showInvoiceSelection}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Appointment for Invoice</Text>
              <TouchableOpacity onPress={() => setShowInvoiceSelection(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.appointmentList}>
              {/* Create New Appointment Button */}
              <TouchableOpacity
                style={styles.createAppointmentButton}
                onPress={handleCreateNewAppointment}
              >
                <Text style={styles.createAppointmentIcon}>➕</Text>
                <View style={styles.createAppointmentText}>
                  <Text style={styles.createAppointmentTitle}>Create New Appointment</Text>
                  <Text style={styles.createAppointmentSubtitle}>For customer not yet on the app</Text>
                </View>
                <Text style={styles.createAppointmentArrow}>→</Text>
              </TouchableOpacity>

              {appointments.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>No existing appointments</Text>
                  <Text style={styles.emptyStateSubtext}>Use "Create New Appointment" above or create appointments in the Appointments tab</Text>
                </View>
              ) : (
                appointments.map((appointment) => (
                  <TouchableOpacity
                    key={appointment.id}
                    style={styles.appointmentItem}
                    onPress={() => handleSelectAppointmentForInvoice(appointment)}
                  >
                    <View style={styles.appointmentItemHeader}>
                      <Text style={styles.appointmentItemName}>{appointment.customerName}</Text>
                      <View style={[styles.statusBadge, { backgroundColor: getStatusColor(appointment.status) }]}>
                        <Text style={styles.statusText}>{getStatusText(appointment.status)}</Text>
                      </View>
                    </View>
                    <View style={styles.appointmentItemDetailRow}>
                      <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                      <Text style={styles.appointmentItemDetails}>{appointment.appointmentDate} at {appointment.appointmentTime}</Text>
                    </View>
                    <Text style={styles.appointmentItemDetails}>
                      🔧 {appointment.serviceType}
                    </Text>
                    <Text style={styles.appointmentItemDetails}>
                      📧 {appointment.email}
                    </Text>
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Invoice Price Input Modal */}
      <Modal
        visible={showInvoicePriceInput}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Set Invoice Price</Text>
              <TouchableOpacity onPress={() => setShowInvoicePriceInput(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.priceInputContainer}>
              <Text style={styles.priceInputSubtitle}>
                Set the price for {selectedAppointmentForInvoice?.customerName}'s {selectedAppointmentForInvoice?.serviceType}
              </Text>

              {/* Service Selection Options */}
              <View style={styles.priceOptionsContainer}>
                <TouchableOpacity
                  style={styles.serviceOptionButton}
                  onPress={() => setShowServiceSelection(true)}
                >
                  <Text style={styles.serviceOptionIcon}>📋</Text>
                  <View style={styles.serviceOptionText}>
                    <Text style={styles.serviceOptionTitle}>Select from Services</Text>
                    <Text style={styles.serviceOptionSubtitle}>
                      {selectedService
                        ? `${selectedService.name} — ${formatServicePriceShort(selectedService.price)}`
                        : 'Choose a service (optional price) or enter amount manually'}
                    </Text>
                  </View>
                  <Text style={styles.serviceOptionArrow}>→</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.serviceOptionButton}
                  onPress={handleManualPriceEntry}
                >
                  <Text style={styles.serviceOptionIcon}>✏️</Text>
                  <View style={styles.serviceOptionText}>
                    <Text style={styles.serviceOptionTitle}>Enter Manual Price</Text>
                    <Text style={styles.serviceOptionSubtitle}>Type in a custom amount</Text>
                  </View>
                  <Text style={styles.serviceOptionArrow}>→</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.priceInputGroup}>
                <Text style={styles.priceInputLabel}>Amount ($)</Text>
                <AppTextInput
                  style={styles.priceInput}
                  value={invoicePrice}
                  onChangeText={setInvoicePrice}
                  placeholder="0.00"
                  keyboardType="numeric"
                  editable={!selectedService || !hasListedServicePrice(selectedService.price)}
                />
              </View>

              <View style={styles.priceInputButtons}>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => setShowInvoicePriceInput(false)}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.confirmButton}
                  onPress={handleConfirmInvoicePrice}
                >
                  <Text style={styles.confirmButtonText}>Continue</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Service Selection Modal */}
      <Modal
        visible={showServiceSelection}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Service</Text>
              <TouchableOpacity onPress={() => setShowServiceSelection(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.serviceList}>
              {services.map((service) => (
                <TouchableOpacity
                  key={service.id}
                  style={styles.serviceItem}
                  onPress={() => handleSelectService(service)}
                >
                  <View style={styles.serviceInfo}>
                    <Text style={styles.serviceName}>{service.name}</Text>
                    <Text style={styles.serviceDetails}>
                      {formatServicePriceShort(service.price)} • {service.duration} minutes
                    </Text>
                  </View>
                  <Text style={styles.serviceArrow}>→</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Create New Appointment Modal */}
      <Modal
        visible={showCreateAppointment}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Create New Appointment</Text>
              <TouchableOpacity onPress={() => setShowCreateAppointment(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.createAppointmentForm}>
              <Text style={styles.createAppointmentSubtitle}>
                Create an appointment for a customer not yet on the app
              </Text>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Customer Name *</Text>
                <AppTextInput
                  style={styles.input}
                  value={newCustomerData.name}
                  onChangeText={(text) => setNewCustomerData({...newCustomerData, name: text})}
                  placeholder="Enter customer name"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Phone Number *</Text>
                <AppTextInput
                  style={styles.input}
                  value={newCustomerData.phone}
                  onChangeText={(text) => setNewCustomerData({...newCustomerData, phone: text})}
                  placeholder="Enter phone number"
                  keyboardType="phone-pad"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Email (Optional)</Text>
                <AppTextInput
                  style={styles.input}
                  value={newCustomerData.email}
                  onChangeText={(text) => setNewCustomerData({...newCustomerData, email: text})}
                  placeholder="customer@example.com"
                  keyboardType="email-address"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Service Type *</Text>
                <AppTextInput
                  style={styles.input}
                  value={newCustomerData.serviceType}
                  onChangeText={(text) => setNewCustomerData({...newCustomerData, serviceType: text})}
                  placeholder="e.g., Haircut, Hair Color, etc."
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Appointment Date</Text>
                <AppTextInput
                  style={styles.input}
                  value={newCustomerData.appointmentDate}
                  onChangeText={(text) => setNewCustomerData({...newCustomerData, appointmentDate: text})}
                  placeholder={new Date().toLocaleDateString()}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Appointment Time</Text>
                <AppTextInput
                  style={styles.input}
                  value={newCustomerData.appointmentTime}
                  onChangeText={(text) => setNewCustomerData({...newCustomerData, appointmentTime: text})}
                  placeholder="10:00 AM"
                />
              </View>

              <View style={styles.createAppointmentButtons}>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => setShowCreateAppointment(false)}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.confirmButton}
                  onPress={handleCreateAppointment}
                >
                  <Text style={styles.confirmButtonText}>Create & Continue</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Invoice Delivery Method Modal */}
      <Modal
        visible={showInvoiceDeliveryMethod}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Choose Delivery Method</Text>
              <TouchableOpacity onPress={() => setShowInvoiceDeliveryMethod(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.deliveryMethodContainer}>
              <Text style={styles.deliveryMethodSubtitle}>
                How would you like to send the invoice to {selectedAppointmentForInvoice?.customerName}?
              </Text>

              <TouchableOpacity
                style={styles.deliveryMethodButton}
                onPress={() => handleInvoiceDeliveryMethod('sms')}
              >
                <Text style={styles.deliveryMethodIcon}>📱</Text>
                <View style={styles.deliveryMethodTextContainer}>
                  <Text style={styles.deliveryMethodTitle}>SMS</Text>
                  <Text style={styles.deliveryMethodDescription}>
                    Send invoice via text message to {selectedAppointmentForInvoice?.phone}
                  </Text>
                </View>
                <Text style={styles.deliveryMethodArrow}>→</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.deliveryMethodButton}
                onPress={() => handleInvoiceDeliveryMethod('chat')}
              >
                <Text style={styles.deliveryMethodIcon}>💬</Text>
                <View style={styles.deliveryMethodTextContainer}>
                  <Text style={styles.deliveryMethodTitle}>In-App Chat</Text>
                  <Text style={styles.deliveryMethodDescription}>
                    Send invoice through the app's chat system
                  </Text>
                </View>
                <Text style={styles.deliveryMethodArrow}>→</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.deliveryMethodButton}
                onPress={() => handleInvoiceDeliveryMethod('email')}
              >
                <Text style={styles.deliveryMethodIcon}>📧</Text>
                <View style={styles.deliveryMethodTextContainer}>
                  <Text style={styles.deliveryMethodTitle}>Email</Text>
                  <Text style={styles.deliveryMethodDescription}>
                    Send professional invoice via email to {selectedAppointmentForInvoice?.email}
                  </Text>
                </View>
                <Text style={styles.deliveryMethodArrow}>→</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <FrontDeskQRModal
        visible={showFrontDeskQRModal}
        onClose={() => setShowFrontDeskQRModal(false)}
        bookingUrl={frontDeskBookingUrl}
        signPdfUrl={frontDeskSignUrl}
        businessName={businessOwnerName || 'your business'}
      />

      {/* Bottom Menu */}
      <BusinessOwnerBottomMenu
        activeTab={activeTab}
        onTabPress={handleTabPress}
        onNavigateToSettings={onNavigateToSettings}
        onLogout={onLogout}
      />

      {/* SMS Test Modal */}
      <SMSTestModal
        visible={showSMSTest}
        onClose={() => setShowSMSTest(false)}
      />

      {/* Trial Expired Modal - DISABLED FOR DEMO */}
      {/* <TrialExpiredModal
        visible={showTrialExpired}
        onUpgrade={handleTrialUpgrade}
        onLogout={handleTrialLogout}
        businessOwnerId={username} // Using username as businessOwnerId for now
        trialData={trialData}
      /> */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100, // Space for bottom menu
  },
  header: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    paddingTop: 50,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: 1,
  },
  greeting: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 2,
  },
  userName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 4,
  },
  planContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  planText: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    marginRight: 8,
  },
  upgradeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  upgradeButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  statsContainer: {
    flexDirection: 'row',
    padding: 20,
    justifyContent: 'space-around',
  },
  statCard: {
    backgroundColor: 'white',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#007bff',
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 5,
  },
  appointmentsContainer: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  appointmentCard: {
    backgroundColor: 'white',
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  appointmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  customerName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  appointmentDetails: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  appointmentActions: {
    flexDirection: 'row',
    marginTop: 15,
    flexWrap: 'wrap',
  },
  actionButton: {
    backgroundColor: '#2563EB', // Professional blue (similar to LinkedIn/PayPal)
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 10,
    marginBottom: 5,
  },
  actionButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  paymentButton: {
    backgroundColor: '#2563EB', // Professional blue
  },
  chatButton: {
    backgroundColor: '#2563EB', // Professional blue
  },
  deleteButton: {
    backgroundColor: '#EF4444', // Red for delete actions
  },
  quickActionsSection: {
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: 'white',
    marginBottom: 10,
  },
  quickActionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginHorizontal: 0,
  },
  quickActionButton: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    paddingHorizontal: 8,
    borderRadius: 12,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#E5E5E7',
    width: '32%',
    marginBottom: 12,
    minHeight: 88,
  },
  quickActionIcon: {
    fontSize: 28,
    marginBottom: 8,
  },
  quickActionText: {
    color: '#1C1C1E',
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  quoteRequestBadge: {
    position: 'absolute',
    top: -4,
    right: -10,
    backgroundColor: '#FF3B30',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quoteRequestBadgeText: {
    color: 'white',
    fontSize: 11,
    fontWeight: '700',
  },
  // Invoice Selection Modal Styles
  appointmentList: {
    maxHeight: 400,
    padding: 10,
  },
  emptyState: {
    alignItems: 'center',
    padding: 40,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 10,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#8E8E93',
    textAlign: 'center',
  },
  appointmentItem: {
    backgroundColor: 'white',
    padding: 15,
    marginBottom: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  appointmentItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  appointmentItemName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  appointmentItemDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  bookaptIconSmall: {
    width: 18,
    height: 18,
    marginRight: 8,
  },
  appointmentItemDetails: {
    fontSize: 14,
    color: '#8E8E93',
  },
  // Invoice Delivery Method Modal Styles
  deliveryMethodContainer: {
    padding: 20,
  },
  deliveryMethodSubtitle: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 20,
    textAlign: 'center',
  },
  deliveryMethodButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    backgroundColor: 'white',
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  deliveryMethodIcon: {
    fontSize: 24,
    marginRight: 15,
  },
  deliveryMethodTextContainer: {
    flex: 1,
  },
  deliveryMethodTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  deliveryMethodDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  deliveryMethodArrow: {
    fontSize: 18,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  // Payment Options Modal Styles
  paymentOptionsContainer: {
    padding: 20,
  },
  paymentOptionsSubtitle: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 20,
    textAlign: 'center',
  },
  paymentMethodsList: {
    gap: 12,
  },
  paymentMethodButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  paymentMethodIcon: {
    fontSize: 24,
    marginRight: 16,
  },
  paymentMethodTextContainer: {
    flex: 1,
  },
  paymentMethodTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  paymentMethodDescription: {
    fontSize: 14,
    color: '#8E8E93',
  },
  paymentMethodArrow: {
    fontSize: 18,
    color: '#8E8E93',
    marginLeft: 10,
  },
  // Invoice Price Input Modal Styles
  priceInputContainer: {
    padding: 20,
  },
  priceInputSubtitle: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 20,
    textAlign: 'center',
  },
  priceInputGroup: {
    marginBottom: 30,
  },
  priceInputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  priceInput: {
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    padding: 12,
    fontSize: 18,
    backgroundColor: 'white',
    textAlign: 'center',
  },
  priceInputButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  cancelButtonText: {
    color: '#6B7280',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  confirmButton: {
    flex: 1,
    backgroundColor: '#7C3AED', // Indigo/Purple primary
    paddingVertical: 12,
    borderRadius: 8,
  },
  confirmButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  // Service Selection Styles
  priceOptionsContainer: {
    marginBottom: 20,
  },
  serviceOptionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    marginBottom: 12,
  },
  serviceOptionIcon: {
    fontSize: 24,
    marginRight: 16,
  },
  serviceOptionText: {
    flex: 1,
  },
  serviceOptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  serviceOptionSubtitle: {
    fontSize: 14,
    color: '#8E8E93',
  },
  serviceOptionArrow: {
    fontSize: 18,
    color: '#8E8E93',
    marginLeft: 10,
  },
  serviceList: {
    maxHeight: 400,
  },
  serviceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  serviceInfo: {
    flex: 1,
  },
  serviceName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  serviceDetails: {
    fontSize: 14,
    color: '#8E8E93',
  },
  serviceArrow: {
    fontSize: 18,
    color: '#8E8E93',
    marginLeft: 10,
  },
  // Create Appointment Styles
  createAppointmentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#E6F2FF',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#007AFF',
    marginBottom: 16,
  },
  createAppointmentIcon: {
    fontSize: 24,
    marginRight: 16,
  },
  createAppointmentText: {
    flex: 1,
  },
  createAppointmentTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
    marginBottom: 4,
  },
  createAppointmentSubtitle: {
    fontSize: 14,
    color: '#007AFF',
    opacity: 0.8,
  },
  createAppointmentArrow: {
    fontSize: 18,
    color: '#007AFF',
    marginLeft: 10,
  },
  createAppointmentForm: {
    padding: 20,
  },
  createAppointmentButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 20,
  },
  poweredByContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingBottom: 20,
    backgroundColor: '#F8F9FA',
    borderTopWidth: 1,
    borderTopColor: '#E5E5E7',
    width: '100%',
  },
  poweredByText: {
    fontSize: 12,
    color: '#999',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  // Web Version Banner Styles
  webVersionBanner: {
    backgroundColor: '#E6F2FF',
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
    marginHorizontal: 20,
    marginTop: 10,
    marginBottom: 10,
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  webVersionBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  webVersionBannerIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  webVersionBannerText: {
    flex: 1,
    marginRight: 12,
  },
  webVersionBannerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  webVersionBannerSubtitle: {
    fontSize: 12,
    color: '#666',
  },
  webVersionBannerButton: {
    backgroundColor: '#7C3AED', // Indigo/Purple primary
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  webVersionBannerButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default DashboardScreen;